import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material'
import React, { ReactNode } from 'react'

import { SkeletonColumnConfig } from '../Form/FormSkeleton'

interface ContainerSkeletonColumnConfig extends SkeletonColumnConfig {
  /** Si se necesita un ancho específico (por ejemplo '60%') */
  width?: string | number
}

export type SkeletonType = 'custom' | 'table' | 'form' | 'card' | 'list' | 'profile'

export interface ContainerSkeletonProps {
  /** Indica si se debe mostrar el esqueleto de carga */
  isLoading: boolean
  /** Contenido a renderizar cuando no está cargando */
  children: ReactNode
  /** Tipo de esqueleto predefinido para generar automáticamente la interfaz */
  type?: SkeletonType
  /** Configuración explícita (solo aplica si type es "custom") */
  layout?: ContainerSkeletonColumnConfig[][]
  /** Número de filas (para types: custom, table, form, list, card) */
  rows?: number
  /** Número de columnas (para types: custom, form, card) */
  cols?: number
  /** Altura estándar (para type: custom, form) */
  inputHeight?: number | string
  /** Ancho estándar (para type: custom, form) */
  inputWidth?: number | string
  /** Espaciado entre elementos */
  gap?: number
}

/**
 * Contenedor inteligente que genera un Skeleton UI o Skeleton Screen
 * de acuerdo al "type" del componente que se está cargando.
 *
 * @example
 * // 1. Ejemplo Básico (Lista de 5 filas):
 * <ContainerSkeleton isLoading={isLoading} type="list" rows={5}>
 *   <MiComponenteLista />
 * </ContainerSkeleton>
 *
 * @example
 * // 2. Ejemplo Formulario (3 filas de 2 columnas):
 * <ContainerSkeleton isLoading={isLoading} type="form" rows={3} cols={2}>
 *   <MiFormulario />
 * </ContainerSkeleton>
 *
 * @example
 * // 3. Ejemplo Custom Layout (Control explícito de filas y proporciones):
 * <ContainerSkeleton
 *   isLoading={isLoading}
 *   type="custom"
 *   layout={[
 *     [{ md: 12, height: 100, variant: 'rectangular' }], // Fila 1: 1 col ancha y alta
 *     [{ md: 4 }, { md: 8 }]                             // Fila 2: 2 cols (proporción 4 y 8 de 12)
 *   ]}
 * >
 *   <MiComponenteCustom />
 * </ContainerSkeleton>
 */
export const ContainerSkeleton: React.FC<ContainerSkeletonProps> = ({
  isLoading,
  children,
  type = 'custom',
  layout,
  rows = 3,
  cols = 2,
  inputHeight = 56,
  inputWidth,
  gap = 1,
}) => {
  if (!isLoading) {
    return <>{children}</>
  }

  // ==== 1. CUSTOM / DEFAULT FORM LAYOUT ====
  if (type === 'custom' || type === 'form') {
    const defaultColWidth = Math.floor(12 / cols) as number
    const finalLayout: ContainerSkeletonColumnConfig[][] =
      layout ||
      Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => ({
          xs: 12,
          md: defaultColWidth,
          height: inputHeight,
          width: inputWidth,
          variant: 'rounded',
        })),
      )

    return (
      <Box sx={{ width: '100%', p: 0 }}>
        <Stack spacing={gap}>
          {finalLayout.map((row, rowIndex) => (
            <Stack key={`row-${rowIndex}`} direction={{ xs: 'column', md: 'row' }} spacing={gap}>
              {row.map((col, colIndex) => (
                <Box
                  key={`row-${rowIndex}-col-${colIndex}`}
                  sx={{ flex: { xs: col.xs || 12, md: col.md || 6 } }}
                >
                  <Skeleton
                    variant={col.variant || 'rounded'}
                    animation="wave"
                    width={col.width || '100%'}
                    height={col.height || inputHeight}
                    sx={{ borderRadius: col.variant === 'text' ? undefined : '4px' }}
                  />
                </Box>
              ))}
            </Stack>
          ))}
        </Stack>
      </Box>
    )
  }

  // ==== 2. TABLE SKELETON ====
  if (type === 'table') {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Header de la tabla */}
        <Skeleton variant="rounded" width="100%" height={60} animation="wave" sx={{ mb: 2 }} />
        {/* Filas de la tabla */}
        <Stack spacing={1.5}>
          {Array.from(new Array(rows > 1 ? rows : 5)).map((_, index) => (
            <Skeleton key={index} variant="rounded" width="100%" height={45} animation="wave" />
          ))}
        </Stack>
      </Box>
    )
  }

  // ==== 3. CARD SKELETON ====
  if (type === 'card') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={gap}>
        {Array.from(new Array(cols > 0 ? cols : 3)).map((_, index) => (
          <Box key={index} sx={{ flex: 1 }}>
            <Card sx={{ width: '100%', boxShadow: 2 }}>
              <Skeleton variant="rectangular" height={140} animation="wave" />
              <CardContent>
                <Skeleton variant="text" width="80%" height={28} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={20} animation="wave" />
                <Skeleton variant="text" width="60%" height={20} animation="wave" />
              </CardContent>
            </Card>
          </Box>
        ))}
      </Stack>
    )
  }

  // ==== 4. LIST SKELETON ====
  if (type === 'list') {
    return (
      <Stack spacing={gap}>
        {Array.from(new Array(rows > 1 ? rows : 4)).map((_, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={50} height={50} animation="wave" />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={24} animation="wave" />
              <Skeleton variant="text" width="20%" height={20} animation="wave" />
            </Box>
          </Box>
        ))}
      </Stack>
    )
  }

  // ==== 5. PROFILE SKELETON ====
  if (type === 'profile') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2 }}>
        <Skeleton variant="circular" width={120} height={120} animation="wave" />
        <Skeleton variant="text" width={200} height={40} animation="wave" />
        <Skeleton variant="text" width={300} height={24} animation="wave" />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Skeleton variant="rounded" width={100} height={36} animation="wave" />
          <Skeleton variant="rounded" width={100} height={36} animation="wave" />
        </Box>
      </Box>
    )
  }

  return null
}
